import React from 'react';

import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';
import {CallMade} from '@material-ui/icons';
import {AppBar, Box, Grid, Link, Typography} from '@material-ui/core';

import {Utils} from 'commons';
import {useAlert, useSpinner} from 'hooks';
import {AppBarStyles} from 'assets/tss/commons';
import {LinkStyles} from 'assets/tss/layouts/components';
import {EfeButtonPrimary, EfeModalMsg} from 'components';
import {ClientService, DocumentServices, ReportsService} from 'services';
import {BottomContent, MainContent} from 'layouts/commons';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import {ReactComponent as DownloadIcon} from 'assets/icons/section/action/download.svg';
import {
    StatusTitles,
    SnackbarVariants,
    Messages,
    Constants,
    HistoryRoutes,
    COMPLEMENT_MODULES,
    DASHBOARD,
} from 'commons/constants';
import {Documents, EfeResponse} from '../../../commons/models';
import {ApiStatus} from 'commons/models/dtos/reports/ApiReport';

type Props = {
    message: string;
    hideDashboard?: boolean;
    userBlocked: boolean;
    dashboardBlocked: boolean;
    expedientId?: number;
    kycId?: number;
    documentKey?: string;
};

function ContractStatus(props: Props) {
    const {
        message,
        userBlocked,
        dashboardBlocked,
        hideDashboard,
        expedientId,
        kycId,
        documentKey,
    } =
        props;

    const history = useHistory();
    const classes = AppBarStyles();
    const classesLink = LinkStyles();
    const {enqueueSnackbar} = useSnackbar();
    const {setLoadUsers} = useRelatedPeopleContext();
    const {updateMenu, fatherKycId} = useMenuContext();
    const {
        showAlert,
        defaultError,
        genericError,
        showAlertError,
        closeAlert,
    } = useAlert();

    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    async function handleDownload(doc: string) {
        setIsLoading(true);
        try {
            if (documentKey && expedientId && kycId) {
                const response = await DocumentServices.getDocumentByKycId(
                    documentKey,
                    expedientId,
                    kycId,
                    'pdf'
                );
                Utils.functions.downloadDocs(response, `${documentKey}.pdf`);
            } else {
                const response = await DocumentServices.getDocument(doc);
                Utils.functions.downloadDocs(response, `${doc}.pdf`);
            }
            enqueueSnackbar(Messages.FILE_DOWNLOAD_OK, {
                variant: SnackbarVariants.SUCCESS,
            });
        } catch (error) {
            try {
                const apiStatus: EfeResponse<ApiStatus> = await ReportsService.validateContractService();
                if (apiStatus && expedientId && documentKey === 'DOC_CONTRATO_CL_PDF') {
                    const response: EfeResponse<Documents> = await ReportsService.generateContract(expedientId);
                    Utils.functions.downloadDocs(response, `${doc}.pdf`);
                    enqueueSnackbar(Messages.FILE_DOWNLOAD_OK, {
                        variant: SnackbarVariants.SUCCESS,
                    });
                } else {
                    showAlertError(error, Messages.CONTRACT_DOWNLOAD_ERROR);
                }
            } catch (e) {
                showAlertError(e, Messages.CONTRACT_SERVICE_ERROR);
            }
        } finally {
            setIsLoading(false);
        }
    }

    async function handleSubmit() {
        setIsLoading(true);
        try {
            const response = await ClientService.getLoadUsers(fatherKycId);
            setIsLoading(false);
            if (response?.data?.data) {
                updateMenu([...COMPLEMENT_MODULES, ...DASHBOARD]);
                history.push(HistoryRoutes.DASHBOARD);
                setLoadUsers([...response.data.data]);
            } else {
                enqueueSnackbar(Messages.LOAD_USERS_ERROR, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            setIsLoading(false);
            showAlertError(error, Messages.LOAD_USERS_ERROR);
        }
    }

    return (
        <>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            <MainContent>
                <Box m={4} mb={2}>
                    <Typography component={'span'} variant="h3">
                        {userBlocked ? StatusTitles.AVISO : StatusTitles.LISTO}
                    </Typography>
                </Box>
                <Box m={4} mb={8}>
                    <Typography component={'span'} variant="h4">
                        {message}
                    </Typography>
                </Box>
                {!userBlocked && (
                    <Box mt={2} mb={2}>
                        <Link
                            className={classesLink.bold}
                            variant="body1"
                            color="secondary"
                            underline="always">
                            <Typography
                                onClick={() => handleDownload(Constants.DOC_CONTRACT)}
                                component={'span'}
                                variant="h5"
                                className={classesLink.bold}>
                                <Box display='flex' component='div' justifyContent='flex-start'>
                                    <Box m={2}>
                                        <DownloadIcon />
                                    </Box>
                                    <Box m={2}>{'Descargar contrato'}</Box>
                                </Box>
                            </Typography>
                        </Link>
                    </Box>
                )}
            </MainContent>
            {!hideDashboard && (
                <AppBar position='fixed' color='primary' className={classes.appBar}>
                    <BottomContent>
                        <Grid container direction='row' justifyContent='flex-start'>
                            <Grid item xs={12}>
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    justifyContent="center"
                                    m={2}>
                                    <Grid item xs={12}>
                                        <Box m={4} mb={8}>
                                            <Box m={4} mb={2}>
                                                <EfeButtonPrimary
                                                    disabled={dashboardBlocked}
                                                    onClick={() => handleSubmit()}
                                                    endIcon={<CallMade />}>
                                                    Ir a dashboard
                                                </EfeButtonPrimary>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Box>
                            </Grid>
                        </Grid>
                    </BottomContent>
                </AppBar>
            )}
        </>
    );
}

export default ContractStatus;
